import { useCallback, useEffect, useRef, useState } from "react";

import type { TranscriptionMetadata } from "@elation/api-client/src/stitch-web";

import { useAppBusEvent } from "@src/features/sse";
import useLatest from "@src/shared/hooks/useLatest";
import { toErrorMessage } from "@src/shared/utils/errors";

import { transcribeVoiceInputAudio } from "../data";
import {
  type UseVoiceInputResult,
  type VoiceInputConfig,
  VoiceInputStateMachine,
  VoiceTranscriptEntity,
} from "../domain";

const PREFERRED_MIME_TYPE = "audio/webm;codecs=opus";
const FALLBACK_MIME_TYPE = "audio/webm";
const RECORDER_TIMESLICE_MS = 1000;
const DEFAULT_TRANSCRIPTION_TIMEOUT_MS = 30_000;
const DEFAULT_PROCESSING_TIMEOUT_MS = 120_000;
const DEFAULT_MAX_RECORDING_DURATION_MS = 240_000;

export interface UseVoiceInputArgs {
  config: VoiceInputConfig;
  onTranscript: (text: string, transcriptionId: string) => void;
  transcriptionTimeoutMs?: number;
  processingTimeoutMs?: number;
  maxRecordingDurationMs?: number;
}

function getSupportedMimeType(): string | undefined {
  if (typeof MediaRecorder === "undefined") {
    return undefined;
  }

  if (MediaRecorder.isTypeSupported(PREFERRED_MIME_TYPE)) {
    return PREFERRED_MIME_TYPE;
  }

  if (MediaRecorder.isTypeSupported(FALLBACK_MIME_TYPE)) {
    return FALLBACK_MIME_TYPE;
  }

  return undefined;
}

async function isMicPermissionPersistentlyDenied(): Promise<boolean> {
  try {
    const status = await navigator.permissions.query({
      name: "microphone" as PermissionName,
    });
    return status.state === "denied";
  } catch {
    return false;
  }
}

async function getPermissionErrorMessage(error: unknown): Promise<string> {
  if (error instanceof DOMException) {
    switch (error.name) {
      case "NotAllowedError": {
        const persistent = await isMicPermissionPersistentlyDenied();
        if (persistent) {
          return "Microphone access is blocked. Use your browser site settings to enable microphone access.";
        }
        return "Microphone access was denied. Please allow microphone access when prompted and try again.";
      }
      case "NotFoundError":
        return "No microphone was found. Please connect a microphone and try again.";
      default:
        return `Microphone error: ${error.message}`;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return "An unknown error occurred while accessing the microphone.";
}

export function useVoiceInput({
  config,
  onTranscript,
  transcriptionTimeoutMs = DEFAULT_TRANSCRIPTION_TIMEOUT_MS,
  processingTimeoutMs = DEFAULT_PROCESSING_TIMEOUT_MS,
  maxRecordingDurationMs = DEFAULT_MAX_RECORDING_DURATION_MS,
}: UseVoiceInputArgs): UseVoiceInputResult {
  const [state, setState] = useState<UseVoiceInputResult["state"]>("idle");
  const [error, setError] = useState<string | null>(null);
  const [pendingTranscript, setPendingTranscript] = useState(
    () => null as ReturnType<typeof VoiceTranscriptEntity.create> | null,
  );
  const pendingTranscriptRef = useRef<ReturnType<
    typeof VoiceTranscriptEntity.create
  > | null>(null);
  const [isSupported] = useState(
    () =>
      typeof window !== "undefined" &&
      typeof navigator !== "undefined" &&
      typeof navigator.mediaDevices !== "undefined" &&
      typeof MediaRecorder !== "undefined",
  );

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const maxDurationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const processingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const transcriptionAbortRef = useRef<AbortController | null>(null);
  const onTranscriptRef = useLatest(onTranscript);
  const configRef = useLatest(config);
  const isCancelledRef = useRef(false);
  const isStartingRef = useRef(false);

  const completePendingTranscript = useCallback(() => {
    if (processingTimerRef.current) {
      clearTimeout(processingTimerRef.current);
      processingTimerRef.current = null;
    }
    pendingTranscriptRef.current = null;
    setPendingTranscript(null);
    setState((current) =>
      VoiceInputStateMachine.transition(current, { type: "COMPLETE" }),
    );
  }, []);

  const failPendingTranscript = useCallback((message: string) => {
    if (processingTimerRef.current) {
      clearTimeout(processingTimerRef.current);
      processingTimerRef.current = null;
    }
    setError(message);
    pendingTranscriptRef.current = null;
    setPendingTranscript(null);
    setState((current) =>
      VoiceInputStateMachine.transition(current, { type: "FAIL" }),
    );
  }, []);

  useAppBusEvent("transcription.succeeded", (payload) => {
    if (
      VoiceTranscriptEntity.matchesTranscriptionEvent(
        pendingTranscriptRef.current,
        payload,
      )
    ) {
      completePendingTranscript();
    }
  });

  useAppBusEvent("transcription.failed", (payload) => {
    if (
      VoiceTranscriptEntity.matchesTranscriptionEvent(
        pendingTranscriptRef.current,
        payload,
      )
    ) {
      failPendingTranscript(payload.error);
    }
  });

  const clearMaxDurationTimer = useCallback(() => {
    if (maxDurationTimerRef.current) {
      clearTimeout(maxDurationTimerRef.current);
      maxDurationTimerRef.current = null;
    }
  }, []);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const getMediaStream = useCallback(() => streamRef.current, []);

  const transcribe = useCallback(
    async (audioBlob: Blob) => {
      const abortController = new AbortController();
      transcriptionAbortRef.current = abortController;
      const timeoutId = setTimeout(() => {
        abortController.abort();
      }, transcriptionTimeoutMs);

      try {
        const result = await transcribeVoiceInputAudio({
          audioBlob,
          metadata: {
            purpose: configRef.current.purpose,
            ...configRef.current.metadata,
          } as TranscriptionMetadata,
          signal: abortController.signal,
        });

        if (!isCancelledRef.current) {
          const transcript = VoiceTranscriptEntity.create(result);
          pendingTranscriptRef.current = transcript;
          setPendingTranscript(transcript);
          if (processingTimerRef.current) {
            clearTimeout(processingTimerRef.current);
          }
          processingTimerRef.current = setTimeout(() => {
            failPendingTranscript(
              "Voice input processing timed out. Please try again.",
            );
          }, processingTimeoutMs);
          setState((current) =>
            VoiceInputStateMachine.transition(current, {
              type: "START_PROCESSING",
            }),
          );
          onTranscriptRef.current(result.text, result.transcriptionId);
        }
      } catch (err) {
        if (isCancelledRef.current) return;

        setError(
          abortController.signal.aborted
            ? "Transcription timed out. Please try again."
            : toErrorMessage(err, "Transcription failed. Please try again."),
        );
        pendingTranscriptRef.current = null;
        setPendingTranscript(null);
        setState((current) =>
          VoiceInputStateMachine.transition(current, { type: "COMPLETE" }),
        );
      } finally {
        clearTimeout(timeoutId);
        if (transcriptionAbortRef.current === abortController) {
          transcriptionAbortRef.current = null;
        }
      }
    },
    [failPendingTranscript, processingTimeoutMs, transcriptionTimeoutMs],
  );

  useEffect(() => {
    return () => {
      isCancelledRef.current = true;
      clearMaxDurationTimer();
      if (processingTimerRef.current) {
        clearTimeout(processingTimerRef.current);
        processingTimerRef.current = null;
      }
      pendingTranscriptRef.current = null;
      transcriptionAbortRef.current?.abort();
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        mediaRecorderRef.current.stop();
      }
      stopStream();
    };
  }, [clearMaxDurationTimer, stopStream]);

  const startRecording = useCallback(async () => {
    if (
      isStartingRef.current ||
      (state !== "idle" && state !== "error") ||
      (mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive")
    ) {
      return;
    }

    if (!isSupported) {
      setError("Voice input is not supported in this browser.");
      setState("error");
      return;
    }

    setError(null);
    isCancelledRef.current = false;
    isStartingRef.current = true;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (isCancelledRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      streamRef.current = stream;

      const mimeType = getSupportedMimeType();
      const recorder = new MediaRecorder(
        stream,
        mimeType ? { mimeType } : undefined,
      );

      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        clearMaxDurationTimer();
        if (mediaRecorderRef.current === recorder) {
          mediaRecorderRef.current = null;
        }

        if (isCancelledRef.current) {
          chunksRef.current = [];
          stopStream();
          pendingTranscriptRef.current = null;
          setPendingTranscript(null);
          setState((current) =>
            VoiceInputStateMachine.transition(current, { type: "COMPLETE" }),
          );
          return;
        }

        const chunks = chunksRef.current;
        chunksRef.current = [];
        stopStream();

        if (chunks.length === 0) {
          setState((current) =>
            VoiceInputStateMachine.transition(current, { type: "COMPLETE" }),
          );
          return;
        }

        const audioBlob = new Blob(chunks, {
          type: mimeType ?? FALLBACK_MIME_TYPE,
        });

        setState((current) =>
          VoiceInputStateMachine.transition(current, {
            type: "START_TRANSCRIBING",
          }),
        );
        void transcribe(audioBlob);
      };

      recorder.start(RECORDER_TIMESLICE_MS);
      setState((current) =>
        VoiceInputStateMachine.transition(current, { type: "START_RECORDING" }),
      );

      maxDurationTimerRef.current = setTimeout(() => {
        maxDurationTimerRef.current = null;
        if (recorder.state !== "inactive") {
          recorder.stop();
        }
      }, maxRecordingDurationMs);
    } catch (err) {
      const message = await getPermissionErrorMessage(err);
      if (!isCancelledRef.current) {
        setError(message);
        setState((current) =>
          VoiceInputStateMachine.transition(current, { type: "FAIL" }),
        );
      }
    } finally {
      isStartingRef.current = false;
    }
  }, [
    clearMaxDurationTimer,
    isSupported,
    maxRecordingDurationMs,
    stopStream,
    state,
    transcribe,
  ]);

  const stopRecording = useCallback(() => {
    clearMaxDurationTimer();
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
  }, [clearMaxDurationTimer]);

  const cancelRecording = useCallback(() => {
    clearMaxDurationTimer();
    if (processingTimerRef.current) {
      clearTimeout(processingTimerRef.current);
      processingTimerRef.current = null;
    }
    isCancelledRef.current = true;
    transcriptionAbortRef.current?.abort();

    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
      return;
    }

    chunksRef.current = [];
    stopStream();
    pendingTranscriptRef.current = null;
    setPendingTranscript(null);
    setState((current) =>
      VoiceInputStateMachine.transition(current, { type: "COMPLETE" }),
    );
  }, [clearMaxDurationTimer, stopStream]);

  const dismissError = useCallback(() => {
    setError(null);
    setState((current) =>
      VoiceInputStateMachine.transition(current, { type: "DISMISS_ERROR" }),
    );
  }, []);

  return {
    state,
    startRecording,
    stopRecording,
    cancelRecording,
    dismissError,
    error,
    isSupported,
    pendingTranscriptionId: pendingTranscript?.id ?? null,
    getMediaStream,
  };
}
